// Agent list — the live left-rail of the dashboard.
//
// Sprint UI-9: rebuilt on shadcn/ui primitives (Button, Input,
// DropdownMenu, SidebarGroup, SidebarGroupLabel). Domain logic
// (archive, rename, delete, fork, context menu, undo toast) is
// preserved from the prior implementation.
// DS.5 (sprint aos-ds-shell, spec §2 D3/D4): rebuilt on the vendored design
// system under the As Oasis skin, element for element as Santi approved in
// docs/ui/mockups/aos-design-system/approved-shell.png (DsShellChrome.tsx's
// composition, ported): DS `Divider` for the chrome lines, `TextField` (sm)
// for the search, `IconButton` (through ds/DsIconButton) for "+" / bell /
// gear, `Badge` (dot) for the bell's signal, `Avatar` for the account
// trigger, `Menu` (through ds/DsMenu, `items[]`) for the account menu,
// `Popover` for the notifications FEED (a scrollable feed with a header
// action and per-row read state is not a menu — D4), `Chip` (filter) for
// the stage filters and ds/StageSection.tsx for the collapsible sections.
// Every store and hook read here is untouched (AC-14); every testid
// survives (AC-12). `ui/sidebar.tsx` lost its last importer here.
//
// Sprint agent-stages — sections are founder-defined workflow stages
// (mockup docs/ui/mockups/02-sidebar-stages.html):
//   * collapsible stage sections (collapsed persists server-side, D2),
//   * filter chips under the search row,
//   * desktop drag & drop between sections via @dnd-kit (mobile moves
//     agents through the long-press menu instead — spec D6),
//   * "Sin estado" inbox + the unchanged "Archivado" section,
//   * pin UI retired (spec D3; recency buckets removed with it).

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/app.store';
import { useThemeStore } from '../stores/theme.store';
import { getSocket } from '../lib/socket';
import type { Agent, Stage } from '../types';
import { AgentListItem } from './AgentListItem';
import { AgentContextMenu } from './AgentContextMenu';
import { ConfirmDialog } from './ConfirmDialog';
import { ManageStagesDialog } from './ManageStagesDialog';
import { forkAgent, undeleteAgent } from '../lib/api';
import { DEFAULT_EFFORT, DEFAULT_MODEL } from '../lib/models';
import { useSettingsStore } from '../stores/settings.store';
import { useActiveWorkspaceStore } from '../stores/active-workspace.store';
import { useProjectsStore } from '../stores/projects.store';
import { useStagesStore } from '../stores/stages.store';
import { isPaletteId, paletteColor } from '../lib/avatar-defaults';
import {
  useGroupedAgents,
  NO_STAGE_KEY,
  ARCHIVED_KEY,
} from '../hooks/useGroupedAgents';
import { useSidebarPagination } from '../hooks/useSidebarPagination';
import { useMediaQuery, MD_QUERY } from '../hooks/useMediaQuery';
import { useUndoToast } from '../hooks/useUndoToast';
import { useToastStore } from '../stores/toast.store';
import { useNotificationsStore } from '../stores/notifications.store';
import { formatRelativeTime } from '../lib/time';
import { useTranslation } from '../i18n';
import { RunnersHealthPill } from './RunnersHealthPill';
import { RunnerPickerModal } from './RunnerPickerModal';
import { UsageModal } from './UsageModal';
import { mapSpawnError } from '../lib/spawn-errors';

import Avatar from '@ds/data-display/Avatar';
import DsBadge from '@ds/data-display/Badge';
import Chip from '@ds/data-display/Chip';
import Divider from '@ds/data-display/Divider';
import TextField from '@ds/data-input/TextField';
import Popover from '@ds/overlays/Popover';
import Icon from '@ds/primitives/Icon';
import { Icon as AppIcon } from './atoms/Icon';
import { Badge } from './atoms/Badge';
import { DsIconButton } from './ds/DsIconButton';
import { DsMenu } from './ds/DsMenu';
import { StageSection } from './ds/StageSection';
import './ds/ds-adapters.css';

export interface SidebarProps {
  onAgentSelected?: () => void;
  onLogout?: () => void;
}

export function Sidebar({ onAgentSelected, onLogout }: SidebarProps) {
  const { t } = useTranslation();
  const activeWorkspaceId = useActiveWorkspaceStore((s) => s.activeWorkspaceId);
  // Sprint 20.3 PR-10 (D17) — global usage-modal open state lives in
  // the app store so the EC-43 NO_RUNNER_AVAILABLE toast can open it
  // from anywhere ("View runner usage" action). Sidebar still owns the
  // mount; only the boolean moves to the store.
  const usageOpen = useAppStore((s) => s.usageModalOpen);
  const setUsageOpen = useAppStore((s) => s.setUsageModalOpen);
  const grouped = useGroupedAgents({ projectId: activeWorkspaceId });
  const { visibleCount, revealMore } = useSidebarPagination();
  const undoToast = useUndoToast();

  // Sprint agent-stages — stage sections state.
  const stages = useStagesStore((s) => s.stages);
  const toggleStageCollapsed = useStagesStore((s) => s.toggleCollapsed);
  const setAgentStage = useAppStore((s) => s.setAgentStage);
  const isDesktop = useMediaQuery(MD_QUERY);
  // Focus filter (chips). Sidebar-local by design — a filter is a
  // transient lens, not persistent workspace state.
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  // Collapse for the two fixed sections; stage sections persist their
  // collapse server-side (spec D2) via the stages store.
  const [localCollapsed, setLocalCollapsed] = useState<Record<string, boolean>>({});
  const [stagesDialog, setStagesDialog] = useState<{
    open: boolean;
    mode: 'manage' | 'create';
    assignAgentId?: string;
  }>({ open: false, mode: 'manage' });
  const [draggingAgent, setDraggingAgent] = useState<Agent | null>(null);
  // PointerSensor distance keeps plain clicks working — a row only
  // becomes a drag after 6 px of travel (Cursor-baseline motion feel).
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const selectedId = useAppStore((s) => s.selectedAgentId);
  const selectAgent = useAppStore((s) => s.selectAgent);
  const markRead = useAppStore((s) => s.markRead);
  const markUnread = useAppStore((s) => s.markUnread);
  const archiveAgent = useAppStore((s) => s.archiveAgent);
  const renameAgent = useAppStore((s) => s.renameAgent);
  const deleteAgent = useAppStore((s) => s.deleteAgent);
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const openComposer = useAppStore((s) => s.openComposer);
  const createAgentOptimistic = useAppStore((s) => s.createAgentOptimistic);
  // Sprint 11b.3 (D-009 / OAC1) — guard against the user clicking "+" twice
  // in <100 ms. Locally tracked here (not in zustand) because the lock has
  // no rendering implications; the button's `disabled` flag does the
  // visual gating.
  const newAgentLockRef = useRef(false);
  const memoryConstrained = useAppStore((s) => s.concurrency.memoryConstrained);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const setSearchQuery = useAppStore((s) => s.setSearchQuery);
  const pushToast = useToastStore((s) => s.push);

  async function performDelete(agent: Agent): Promise<void> {
    try {
      await deleteAgent(agent.id);
    } catch {
      return;
    }
    pushToast({
      message: t('toast.agentDeleted'),
      action: {
        label: t('toast.undo'),
        onClick: () => {
          upsertAgent(agent);
          void undeleteAgent(agent.id)
            .then(upsertAgent)
            .catch(() => {
              pushToast({
                message: t('toast.undoFailed'),
                durationMs: 8_000,
              });
            });
        },
      },
    });
  }

  const [menu, setMenu] = useState<{
    agent: Agent;
    point: { x: number; y: number } | null;
  } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Agent | null>(null);
  // Sprint runner-picker-on-create — "+" opens the modal first; the
  // actual createAgentOptimistic call fires from onConfirm with the
  // chosen runnerId (undefined = Auto).
  const [pickerOpen, setPickerOpen] = useState(false);

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  function handleSelect(a: Agent) {
    selectAgent(a.id);
    if (a.unread) markRead(a.id);
    onAgentSelected?.();
  }

  function handleNewAgent() {
    // OAC1 — debounce the "+" button at 200 ms. A second click while a
    // create is in flight would open two modals (and on confirm, two
    // backend creates).
    if (newAgentLockRef.current) return;
    newAgentLockRef.current = true;
    setTimeout(() => {
      newAgentLockRef.current = false;
    }, 200);

    // Sprint runner-picker-on-create — "+" now opens the picker first.
    // The actual createAgentOptimistic call fires from onConfirm so the
    // founder can pin a runner (or stay on Auto). The optimistic +
    // error-handling path lives in createWithRunner below — identical
    // to the pre-sprint flow when runnerId === undefined.
    setPickerOpen(true);
  }

  function createWithRunner(runnerId: string | undefined) {
    // Optimistic path: select the temp agent immediately so the composer
    // opens against the placeholder row in <16 ms. The real `POST
    // /api/agents/empty` resolves in the background; the store swaps the
    // row when it lands.
    // Send the canonical model + effort explicitly so the backend never
    // falls back to its own default. Even though the backend default is
    // also canonical post-sprint fix-opus-alias-drift, sending explicit
    // values keeps the chip-vs-CLI alias resolution single-sourced from
    // `web/src/lib/models.ts` and removes any silent path where a CLI
    // alias could enter the system.
    const { tempId, promise } = createAgentOptimistic({
      model: DEFAULT_MODEL,
      effort: DEFAULT_EFFORT,
      permissionMode: useSettingsStore.getState().defaultPermissionMode,
      projectId: useActiveWorkspaceStore.getState().activeWorkspaceId ?? undefined,
      runnerId,
    });
    selectAgent(tempId);
    onAgentSelected?.();

    promise.catch((err: unknown) => {
      // OAC6 — surface the failure inline. The store has already
      // removed the placeholder row + cleared selection.
      // Sprint S20.5 PR-3 (D013-EC-69) — every backend `code` now
      // routes through `mapSpawnError` so the toast copy + intent
      // stay differentiated by branch:
      //   - NO_RUNNER_AVAILABLE → "View runner usage" → opens modal.
      //   - RUNNER_NOT_READY / RUNNER_OFFLINE → "Tap to retry".
      //   - CLAUDE_NOT_AUTHENTICATED → silent (operator must run
      //     `claude /login` on the runner; no UI retry helps).
      //   - unknown code → GENERIC + retry (legacy behaviour).
      const mapping = mapSpawnError(err);
      const message = t(mapping.i18nKey);
      if (mapping.intent === 'usage') {
        pushToast({
          message,
          durationMs: 8_000,
          action: {
            label: t('toast.viewRunnerUsage'),
            onClick: () => setUsageOpen(true),
          },
        });
      } else if (mapping.intent === 'silent') {
        pushToast({ message, durationMs: 10_000 });
      } else {
        pushToast({
          message,
          action: {
            label: t('toast.retry'),
            // Retry stays on the same runner (or Auto) the founder
            // already chose — no need to re-open the picker.
            onClick: () => createWithRunner(runnerId),
          },
        });
      }
      // openComposer() stays as the manual-fallback affordance the user
      // can still trigger from the empty state.
      void openComposer;
    });
  }

  function handleArchive(agent: Agent) {
    void archiveAgent(agent.id, true);
    undoToast({
      message: t('toast.agentArchived'),
      undoAction: () => archiveAgent(agent.id, false),
    });
  }

  function handleRestore(agent: Agent) {
    void archiveAgent(agent.id, false);
    undoToast({
      message: t('toast.agentRestored'),
      undoAction: () => archiveAgent(agent.id, true),
    });
  }

  function handleDeleteArchived(agent: Agent) {
    void performDelete(agent);
  }

  // ── Sprint agent-stages — dnd handlers (desktop only) ──────────────
  function handleDragStart(e: DragStartEvent) {
    const a = useAppStore.getState().agents[String(e.active.id)];
    setDraggingAgent(a ?? null);
  }

  function handleDragEnd(e: DragEndEvent) {
    setDraggingAgent(null);
    if (e.over == null) return;
    const overKey = String(e.over.id);
    const agentId = String(e.active.id);
    const agent = useAppStore.getState().agents[agentId];
    if (!agent) return;
    const nextStageId = overKey === NO_STAGE_KEY ? null : overKey;
    if ((agent.stageId ?? null) === nextStageId) return;
    void setAgentStage(agentId, nextStageId);
  }

  function renderAgentRow(a: Agent, opts: { archived: boolean; stage: Stage | null }) {
    const stageColorHex =
      opts.stage && isPaletteId(opts.stage.color)
        ? paletteColor(opts.stage.color).bg
        : null;
    const item = (
      <AgentListItem
        agent={a}
        selected={selectedId === a.id}
        renaming={renamingId === a.id}
        archived={opts.archived}
        stageColorHex={stageColorHex}
        onSelect={() => handleSelect(a)}
        onContextMenu={(p) => setMenu({ agent: a, point: p })}
        onRenameSubmit={(name) => {
          void renameAgent(a.id, name);
          setRenamingId(null);
        }}
        onRenameCancel={() => setRenamingId(null)}
        onRenameStart={() => setRenamingId(a.id)}
        onArchive={() => handleArchive(a)}
        onRestore={() => handleRestore(a)}
        onDelete={() => {
          if (a.archived) {
            handleDeleteArchived(a);
          } else {
            setConfirmDelete(a);
          }
        }}
      />
    );
    if (opts.archived) return <div key={a.id}>{item}</div>;
    return (
      <DraggableAgentRow key={a.id} agentId={a.id} disabled={!isDesktop || renamingId === a.id}>
        {item}
      </DraggableAgentRow>
    );
  }

  const isSearching = searchQuery.trim().length > 0;
  const hasAnyAgents =
    grouped.sections.some((sec) => sec.agents.length > 0) ||
    grouped.archived.length > 0;
  const visibleSections = grouped.sections.filter(
    (sec) => stageFilter === null || sec.key === stageFilter,
  );

  return (
    <aside
      className="flex flex-col h-full w-full bg-background border-r border-border overflow-hidden"
      data-testid="sidebar"
    >
      {/* Header — Sprint UI compaction (2026-05-11):
          - The redundant "Agents" label was replaced by `RunnersHealthPill`
            (read-only summary of runner capacity + click → UsageModal).
          - The vestigial `ServerSelector` (post-D-013 it had no function
            because the dashboard is centralised on one control plane)
            was deleted along with its row.
          - The Usage 📊 button was deleted on 2026-05-11 (same-day
            follow-up): it opened the exact same UsageModal as the
            adjacent `RunnersHealthPill`, so the two affordances were
            duplicates sitting next to each other. The pill stays as
            the single entry point — Santi flagged the duplication
            within hours of the previous PR landing in prod. The
            EC-43 NO_RUNNER_AVAILABLE toast still opens the modal via
            its inline "View runner usage" action (see `handleNewAgent`),
            which is the only other entry path that mattered. */}
      {/* Sprint project-identity-visibility (D4/D5) — active project
          name heads the sidebar (Slack pattern: the workspace name is
          the top row, exactly where Santi pointed: "donde pone No
          runners online"). One row cannot hold title + pill + trio at
          the 280 px default width (the trio alone is ~140 px; both
          texts crushed to a glyph — tried and rejected during this
          sprint), so the pill moved to its own status row below the
          title row, where its full label fits. `empty:hidden` keeps
          the row artifact-free when the pill renders null
          (pre-hydration hide-when-empty contract). */}
      <header className="shrink-0 pt-safe-top" data-testid="sidebar-header">
        <div className="h-12 px-3 flex items-center justify-between gap-2">
          <ActiveProjectTitle />
          <div className="flex items-center gap-1 ml-auto">
            <NotificationsButton />
            <SettingsButton />
            <AvatarMenu onLogout={onLogout} />
          </div>
        </div>
        {/* ml-1 → the pill's own px-2 lands its label at the same
            12 px left edge as the title above (px-3). -mt-2 tightens
            the pair against the h-12 row's bottom slack. `inline-flex`
            (not block): approved-shell.png's status row is an inline
            box on the header's line box (DsShellChrome.tsx), and the
            same box here reproduces its geometry to the pixel. */}
        <div className="inline-flex -mt-2 mb-1 ml-1 empty:hidden">
          <RunnersHealthPill />
        </div>
        {/* Sprint add-project-autoprovision — deploy-key provisioning
            status for the ACTIVE project. Same status-row slot +
            visual grammar as RunnersHealthPill (dot + caption).
            Renders null when the active project has no provisioning
            state or it completed cleanly. */}
        <div className="px-1 pb-1 -mt-1 empty:hidden">
          <ProjectProvisioningPill />
        </div>
        <Divider />
      </header>

      {/* Search + New Agent — DS `TextField` (sm) + DS `IconButton` (filled),
          the composition of approved-shell.png (D3: the PNG shows the
          field without a leading glyph, so `TextField`, not `SearchBar`). */}
      <div className="px-3 pt-2 pb-2 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <TextField
            fieldSize="sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('sidebar.search')}
            aria-label={t('sidebar.search')}
            data-testid="agent-search-input"
          />
        </div>
        <DsIconButton
          variant="filled"
          size="sm"
          onClick={handleNewAgent}
          disabled={memoryConstrained}
          data-testid="sidebar-new-agent-button"
          aria-label={t('sidebar.newAgent')}
          title={
            memoryConstrained
              ? t('sidebar.newAgentDisabled')
              : t('tooltip.newAgent')
          }
          className="shrink-0"
        >
          <Icon name="add" size={16} />
        </DsIconButton>
      </div>
      <Divider />

      {/* Sprint agent-stages — filter chips (focus one stage; tap again
          or "Todos" to clear). Hidden while no stages exist. DS `Chip`
          (filter): the selected one carries the piece's own tick. */}
      {stages.length > 0 && (
        <div
          className="px-3 pt-2 pb-1 flex items-center gap-1.5 overflow-x-auto shrink-0"
          data-testid="stage-filter-chips"
        >
          <Chip
            variant="filter"
            size="xs"
            label={t('stages.filterAll')}
            selected={stageFilter === null}
            onClick={() => setStageFilter(null)}
            data-testid="stage-chip-all"
          />
          {stages.map((s) => (
            <Chip
              key={s.id}
              variant="filter"
              size="xs"
              label={s.name}
              selected={stageFilter === s.id}
              icon={<StageFilterDot hex={isPaletteId(s.color) ? paletteColor(s.color).bg : undefined} />}
              onClick={() => setStageFilter((cur) => (cur === s.id ? null : s.id))}
              data-testid={`stage-chip-${s.id}`}
            />
          ))}
        </div>
      )}

      {/* Agent list */}
      <div className="flex-1 overflow-y-auto" data-testid="agent-list">
        {!hasAnyAgents ? (
          isSearching ? (
            <div
              className="px-4 py-8 text-body text-muted-foreground leading-relaxed"
              data-testid="agent-list-no-results"
            >
              <p>{t('common.searchNoMatch', { query: searchQuery.trim() })}</p>
            </div>
          ) : (
            <div
              className="px-4 py-8 text-body text-muted-foreground leading-relaxed"
              data-testid="agent-list-empty"
            >
              <p className="mb-1 font-550">{t('sidebar.empty.title')}</p>
              <p className="text-caption text-muted-foreground/70">
                {t('sidebar.empty.subtitle')}
              </p>
            </div>
          )
        ) : (
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDraggingAgent(null)}
          >
            <div className="py-1">
              {visibleSections.map((sec) => (
                <StageSection
                  key={sec.key}
                  sectionKey={sec.key}
                  label={sec.stage ? sec.stage.name : t('stages.noStage')}
                  dotHex={
                    sec.stage && isPaletteId(sec.stage.color)
                      ? paletteColor(sec.stage.color).bg
                      : undefined
                  }
                  hollowDot={!sec.stage}
                  agents={sec.agents}
                  collapsed={
                    sec.stage ? sec.stage.collapsed : !!localCollapsed[NO_STAGE_KEY]
                  }
                  onToggleCollapsed={() =>
                    sec.stage
                      ? toggleStageCollapsed(sec.stage.id)
                      : setLocalCollapsed((c) => ({
                          ...c,
                          [NO_STAGE_KEY]: !c[NO_STAGE_KEY],
                        }))
                  }
                  droppable={isDesktop}
                  showWhenEmpty={!isSearching}
                  showSignals
                  visibleLimit={visibleCount(sec.key)}
                  showMoreLabel={(count) => t('sidebar.showMore', { count })}
                  onRevealMore={() => revealMore(sec.key)}
                  renderRow={(a) => renderAgentRow(a, { archived: false, stage: sec.stage })}
                />
              ))}
              {stageFilter === null && (
                <StageSection
                  key={ARCHIVED_KEY}
                  sectionKey={ARCHIVED_KEY}
                  label={t('sidebar.section.archived')}
                  agents={grouped.archived}
                  collapsed={!!localCollapsed[ARCHIVED_KEY]}
                  onToggleCollapsed={() =>
                    setLocalCollapsed((c) => ({
                      ...c,
                      [ARCHIVED_KEY]: !c[ARCHIVED_KEY],
                    }))
                  }
                  droppable={false}
                  showWhenEmpty={false}
                  showSignals={false}
                  visibleLimit={visibleCount(ARCHIVED_KEY)}
                  showMoreLabel={(count) => t('sidebar.showMore', { count })}
                  onRevealMore={() => revealMore(ARCHIVED_KEY)}
                  renderRow={(a) => renderAgentRow(a, { archived: true, stage: null })}
                />
              )}
            </div>
            <DragOverlay dropAnimation={null}>
              {draggingAgent ? (
                <DragCard agent={draggingAgent} stages={stages} />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {menu ? (
        <AgentContextMenu
          agent={menu.agent}
          position={menu.point}
          onClose={() => setMenu(null)}
          onMarkRead={() => markRead(menu.agent.id)}
          onMarkUnread={() => void markUnread(menu.agent.id)}
          onSetStage={(stageId) => void setAgentStage(menu.agent.id, stageId)}
          onNewStage={() =>
            setStagesDialog({ open: true, mode: 'create', assignAgentId: menu.agent.id })
          }
          onManageStages={() => setStagesDialog({ open: true, mode: 'manage' })}
          onArchive={() => void archiveAgent(menu.agent.id, true)}
          onRestore={() => void archiveAgent(menu.agent.id, false)}
          onRename={() => setRenamingId(menu.agent.id)}
          onDelete={() => {
            if (menu.agent.archived) {
              void performDelete(menu.agent);
            } else {
              setConfirmDelete(menu.agent);
            }
          }}
          onFork={() => {
            void forkAgent(menu.agent.id).catch(() => {});
          }}
        />
      ) : null}

      <ManageStagesDialog
        open={stagesDialog.open}
        mode={stagesDialog.mode}
        onOpenChange={(open) => setStagesDialog((d) => ({ ...d, open }))}
        onCreated={(stage) => {
          if (stagesDialog.assignAgentId) {
            void setAgentStage(stagesDialog.assignAgentId, stage.id);
          }
        }}
      />

      <UsageModal open={usageOpen} onOpenChange={setUsageOpen} />

      <RunnerPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onConfirm={(runnerId) => createWithRunner(runnerId)}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title={t('confirm.deleteAgentSidebar.title')}
        body={
          confirmDelete
            ? t('confirm.deleteAgentSidebar.bodyTemplate', { name: confirmDelete.name })
            : undefined
        }
        confirmLabel={t('confirm.deleteAgentSidebar.confirm')}
        destructive
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          const target = confirmDelete;
          setConfirmDelete(null);
          if (target) void performDelete(target);
        }}
      />
    </aside>
  );
}

// ── Sprint agent-stages — section + dnd building blocks ─────────────────

// The collapsible section (a stage, "Sin estado", or "Archivado") is
// ds/StageSection.tsx (DS.5) — same props, same testids, same behaviour.

// Drag source wrapper. PointerSensor's 6 px activation distance keeps
// plain clicks (select agent) working; `touch-action` stays default so
// mobile scrolling is untouched (drag is desktop-only — spec D6).
function DraggableAgentRow({
  agentId,
  disabled,
  children,
}: {
  agentId: string;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: agentId,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role={undefined}
      tabIndex={-1}
      className={isDragging ? 'opacity-40' : undefined}
      data-testid={`draggable-agent-${agentId}`}
    >
      {children}
    </div>
  );
}

// Floating card rendered in the DragOverlay while a row is being dragged.
function DragCard({ agent, stages }: { agent: Agent; stages: Stage[] }) {
  const stage = stages.find((s) => s.id === agent.stageId);
  const hex = stage && isPaletteId(stage.color) ? paletteColor(stage.color).bg : null;
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-popover px-3 py-2 shadow-lg cursor-grabbing">
      <span
        className="w-0.5 self-stretch rounded-full shrink-0"
        style={hex ? { backgroundColor: hex } : undefined}
        aria-hidden="true"
      />
      <span className="truncate text-body text-foreground">{agent.name}</span>
    </div>
  );
}

// Stage filter chip (D6) — the DS `Chip` (filter, xs) carries the chip;
// only the stage's colour dot is ours (the piece's `icon` slot).
function StageFilterDot({ hex }: { hex?: string }) {
  if (!hex) return null;
  return (
    <span
      className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
      style={{ backgroundColor: hex }}
      aria-hidden="true"
    />
  );
}

// Sprint project-identity-visibility (D4/D5) — active project name as
// the sidebar-header title. Derived render of the two canonical stores
// (active-workspace + projects); unresolved id (pre-hydration, archived
// mid-session before healActiveWorkspace runs) → render nothing, the
// header degrades to the pre-sprint layout.
// Sprint add-project-autoprovision — founder-mode status line for the
// active project's deploy-key provisioning. Three states:
//   - provisioning → warning dot + "Preparing project…"
//   - failed       → error dot + the backend's honest founder copy
//                    (single home: founderCopyForProvisionFailure).
//   - ready / none → renders null (empty:hidden collapses the row).
// The `tag_listing_lagged` warning is intentionally NOT surfaced —
// it degrades distribution latency, not correctness (developer-layer
// detail; it lives in the control-plane log).
function ProjectProvisioningPill() {
  const { t } = useTranslation();
  const activeId = useActiveWorkspaceStore((s) => s.activeWorkspaceId);
  const state = useProjectsStore((s) =>
    activeId ? s.provisioningById[activeId] : undefined,
  );

  if (!state || state.phase === 'ready') return null;

  const failed = state.phase === 'failed';
  return (
    <div
      className="inline-flex items-start gap-1.5 px-2 py-1 text-caption text-foreground min-w-0"
      data-testid="project-provisioning-pill"
      data-state={state.phase}
    >
      <Badge
        variant="dot"
        color={failed ? 'error' : 'warning'}
        className="w-2 h-2 shrink-0 mt-1"
      />
      <span className={failed ? 'min-w-0' : 'truncate font-550'}>
        {failed
          ? (state.message ?? t('projectProvisioning.failedFallback'))
          : t('projectProvisioning.preparing')}
      </span>
    </div>
  );
}

function ActiveProjectTitle() {
  const activeId = useActiveWorkspaceStore((s) => s.activeWorkspaceId);
  const name = useProjectsStore(
    (s) => s.projects.find((p) => p.id === activeId)?.name ?? null,
  );

  if (name === null) return null;

  return (
    <span
      className="text-body font-550 text-foreground truncate min-w-0"
      data-testid="sidebar-project-title"
    >
      {name}
    </span>
  );
}

// ── Sidebar header trio — DS pieces (DS.5) ───────────────────────────────

// Sprint notification-center (spec D5) — the bell is now the persistent
// feed. The dot lights for either signal: per-agent unread turns (the
// pre-existing behaviour) OR unread feed rows. The panel lists the
// feed newest-first; tapping a row marks it read and opens the agent.
const TIER_ICON: Record<'A' | 'B' | 'C', 'ShieldAlert' | 'CircleCheck' | 'AlertTriangle'> = {
  A: 'ShieldAlert',
  B: 'CircleCheck',
  C: 'AlertTriangle',
};

// The DS `Popover` (position="bottom") centres itself on its anchor, so the
// anchor is placed under the bell and, once the panel has mounted and its
// real width is known, clamped so the panel never leaves the viewport on the
// left — the same collision shift the Radix menu applied (it too overlapped
// the workspace rail at the default sidebar width). Runtime geometry, not a
// design value; the panel's width is read from the panel itself (one source).
const VIEWPORT_EDGE_PX = 8;

// DS `Badge` (dot, danger) is the bell's signal, as approved-shell.png shows
// it. The literal testid marks "the dot is showing" and lives on the piece's
// wrapper (its only attribute slot) — present only while the dot is.
function NotificationsDot({ show, children }: { show: boolean; children: ReactNode }) {
  if (show) {
    return (
      <DsBadge variant="dot" color="danger" data-testid="topbar-notifications-dot">
        {children}
      </DsBadge>
    );
  }
  return (
    <DsBadge variant="dot" color="danger" visible={false}>
      {children}
    </DsBadge>
  );
}

function NotificationsButton() {
  const { t } = useTranslation();
  const selectAgent = useAppStore((s) => s.selectAgent);
  const unreadEventCount = useAppStore((s) => {
    let n = 0;
    for (const id in s.agents) {
      const a = s.agents[id];
      // Sprint dispatch-lineage-ui (D2) — a dispatched worker's unread
      // turn is its CALLER's business (the report-back), never a dot on
      // the founder's bell.
      if (a?.unread && !a.dispatchedBy) n += 1;
    }
    return n;
  });
  const notifications = useNotificationsStore((s) => s.notifications);
  const feedUnread = useNotificationsStore((s) => s.unreadCount);
  const hydrate = useNotificationsStore((s) => s.hydrate);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  const showDot = unreadEventCount > 0 || feedUnread > 0;

  // D4 — the feed is a DS `Popover` opened from a `position: fixed` anchor
  // under the bell: the piece positions inline (no portal, no collision
  // flip), and the sidebar column clips its overflow, so an inline panel
  // wider than the 280 px column would be cut — the fixed anchor escapes
  // every ancestor overflow box (ds5-shell.spec.ts asserts the open panel is
  // fully painted inside the viewport). The bell is not the piece's trigger
  // (that is the invisible anchor), so the bell TOGGLES on `open`: while
  // open, its mousedown is stopped before the piece's document listener
  // (which would close on any outside press) so the click that follows is
  // the one that closes — no half-toggle, no dead click; Enter/Space reach
  // the same click handler.
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  function openFeed() {
    const r = bellRef.current?.getBoundingClientRect();
    setAnchor({ x: r ? r.left + r.width / 2 : 0, y: r ? r.bottom : 0 });
    setOpen(true);
  }

  // Clamp to the viewport's left edge from the panel's REAL width, read after
  // it mounts (the piece centres on the anchor).
  useLayoutEffect(() => {
    if (!open) return;
    const panel = anchorRef.current?.querySelector('[role="dialog"]');
    if (!panel) return;
    const half = panel.getBoundingClientRect().width / 2;
    setAnchor((a) => (a && a.x < half + VIEWPORT_EDGE_PX ? { ...a, x: half + VIEWPORT_EDGE_PX } : a));
  }, [open]);

  return (
    <>
      <NotificationsDot show={showDot}>
        <DsIconButton
          ref={bellRef}
          variant="standard"
          size="sm"
          aria-label={t('topbar.notifications')}
          aria-expanded={open}
          data-testid="topbar-notifications-button"
          onMouseDown={(e) => {
            if (open) e.stopPropagation();
          }}
          onClick={() => (open ? setOpen(false) : openFeed())}
        >
          <Icon name="notifications" size={16} />
        </DsIconButton>
      </NotificationsDot>
      {open && anchor && (
        <div
          ref={anchorRef}
          className="fixed aos-ds-anchor"
          style={{
            left: anchor.x,
            // The piece opens 8 px below its anchor; one --space-1 up gives the 4 px gap.
            top: `calc(${anchor.y}px - var(--space-1))`,
            width: 0,
            height: 0,
            zIndex: 'var(--z-scrim)',
          }}
        >
          <Popover
            open
            onOpenChange={setOpen}
            position="bottom"
            trigger={<span aria-hidden="true" />}
            data-testid="topbar-notifications-panel"
          >
            {/* The piece pads its content by --space-4; the feed wants its
                own row padding, so the panel body cancels it. */}
            <div className="w-80 -m-4">
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-caption text-muted-foreground uppercase">
                  {t('notifications.title')}
                </span>
                {feedUnread > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      markAllRead();
                    }}
                    className="text-caption text-muted-foreground hover:text-foreground py-2 px-2 -my-2 -mx-2"
                    style={{ minHeight: 'auto', minWidth: 'auto' }}
                    data-testid="notifications-mark-all-read"
                  >
                    {t('notifications.markAllRead')}
                  </button>
                )}
              </div>
              <Divider />
              {notifications.length === 0 ? (
                <div className="px-3 py-3 text-caption text-muted-foreground" data-testid="notifications-empty">
                  {unreadEventCount > 0
                    ? t('topbar.unreadAgents', { count: unreadEventCount })
                    : t('topbar.noNotifications')}
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto" data-testid="notifications-feed">
                  {notifications.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      className="w-full text-left flex items-start gap-2.5 px-3 py-2.5 min-h-touch-target cursor-pointer hover:bg-accent/40 focus-visible:bg-accent/40 outline-none"
                      data-testid="notification-row"
                      data-notification-id={n.id}
                      onClick={() => {
                        markRead(n.id);
                        if (n.agentId) selectAgent(n.agentId);
                        setOpen(false);
                      }}
                    >
                      <AppIcon
                        name={TIER_ICON[n.tier]}
                        size={16}
                        className={`shrink-0 mt-0.5 ${
                          n.tier === 'A'
                            ? 'text-status-warning'
                            : n.tier === 'C'
                              ? 'text-status-error'
                              : 'text-muted-foreground'
                        }`}
                        aria-hidden="true"
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-body leading-snug ${
                            n.read ? 'text-muted-foreground' : 'text-foreground'
                          }`}
                        >
                          {n.title}
                        </div>
                        {n.body && (
                          <div className="text-caption text-muted-foreground truncate mt-0.5">
                            {n.body}
                          </div>
                        )}
                        <div className="text-caption text-muted-foreground/70 mt-0.5">
                          {formatRelativeTime(n.createdAt)}
                        </div>
                      </div>
                      {!n.read && (
                        <span
                          className="block h-2 w-2 rounded-full bg-accent shrink-0 mt-1.5"
                          aria-hidden="true"
                          data-testid="notification-unread-dot"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Popover>
        </div>
      )}
    </>
  );
}

function SettingsButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <DsIconButton
      variant="standard"
      size="sm"
      onClick={() => navigate('/settings')}
      aria-label={t('topbar.settings')}
      data-testid="topbar-settings-button"
    >
      <Icon name="settings" size={16} />
    </DsIconButton>
  );
}

// The account avatar is the DS `Avatar` (initials + name-hashed tint under
// the skin), as approved-shell.png shows it. Single-user product: the
// initial was already a literal "S" here; the DS piece derives it from the
// founder's name (DsShellChrome.tsx's FOUNDER_NAME), which also fixes the tint.
const ACCOUNT_AVATAR_NAME = 'Santi';

function AvatarMenu({ onLogout }: { onLogout?: () => void }) {
  const { t } = useTranslation();
  const connection = useAppStore((s) => s.connectionStatus);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggle);

  const connectionLabel =
    connection === 'connected'
      ? t('connection.connected')
      : connection === 'reconnecting'
        ? t('connection.reconnecting')
        : t('connection.disconnected');

  const dotColor: 'success' | 'warning' | 'error' =
    connection === 'connected'
      ? 'success'
      : connection === 'reconnecting'
        ? 'warning'
        : 'error';

  const handleReconnect = () => {
    if (connection !== 'disconnected') return;
    try {
      getSocket().connect();
    } catch {
      /* socket may not exist in test envs */
    }
  };

  return (
    <DsMenu
      align="right"
      data-testid="topbar-avatar-menu"
      trigger={
        <button
          type="button"
          aria-label={t('topbar.accountMenu')}
          className="flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer"
          data-testid="topbar-avatar-button"
        >
          <Avatar name={ACCOUNT_AVATAR_NAME} size="xs" />
        </button>
      }
      items={[
        {
          label: (
            <span data-testid="topbar-connection-item" data-status={connection}>
              {connectionLabel}
            </span>
          ),
          // The connection dot stays the atoms `Badge`: the DS `Badge` has
          // no warning colour for the reconnecting state (§6).
          icon: <Badge variant="dot" color={dotColor} className="w-2.5 h-2.5" />,
          onClick: handleReconnect,
        },
        {
          label: (
            <span data-testid="topbar-theme-toggle" data-theme={theme}>
              {t('topbar.theme')}
            </span>
          ),
          icon: <Icon name={theme === 'dark' ? 'dark_mode' : 'light_mode'} size={16} />,
          onClick: toggleTheme,
        },
        ...(onLogout
          ? [
              { label: null, divider: true },
              {
                label: <span data-testid="logout-button">{t('topbar.signOut')}</span>,
                icon: <Icon name="logout" size={16} />,
                onClick: onLogout,
              },
            ]
          : []),
      ]}
    />
  );
}
